import { Decimal, type CreditLineSummary } from '@paguz/contracts';
import { Badge, MoneyText } from '@paguz/ui';
import { formatBusinessDate } from '../../lib/format.ts';
import { LINE_STATUS } from '../../lib/labels.ts';

/** Porcentaje de uso del cupo (presentación). */
export function usagePercent(
  line: Pick<CreditLineSummary, 'approvedLimit' | 'availableLimit'>,
): number {
  const approved = new Decimal(line.approvedLimit.amount);
  if (approved.lte(0)) return 0;
  const used = approved.minus(line.availableLimit.amount);
  return used.dividedBy(approved).times(100).toDecimalPlaces(0).toNumber();
}

export function UsageBar({ line }: { line: CreditLineSummary }) {
  const percent = usagePercent(line);
  return (
    <div>
      <div
        className="h-2.5 overflow-hidden rounded-full bg-muted"
        role="meter"
        aria-label="Uso del cupo"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={percent >= 90 ? 'h-full bg-warning' : 'h-full bg-highlight'}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{percent} % del cupo en uso</p>
    </div>
  );
}

export function LineStatusBadge({ line }: { line: CreditLineSummary }) {
  const status = LINE_STATUS[line.status];
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Badge variant={status.tone}>{status.label}</Badge>
      {line.dpd > 0 && <Badge variant="danger">{line.dpd} días de mora</Badge>}
    </span>
  );
}

export function LineFigures({ line }: { line: CreditLineSummary }) {
  return (
    <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <Figure label="Cupo aprobado" value={line.approvedLimit.amount} />
      <Figure label="Disponible" value={line.availableLimit.amount} emphasis />
      <Figure label="Utilizado" value={line.usedPrincipal.amount} />
      <Figure label="Retenido (holds)" value={line.heldAmount.amount} />
      <div>
        <dt className="text-xs font-semibold text-muted-foreground">Pago mínimo</dt>
        <dd className="mt-1">
          <MoneyText value={line.minimumPayment.amount} size="md" />
        </dd>
      </div>
      <div>
        <dt className="text-xs font-semibold text-muted-foreground">Próximo corte</dt>
        <dd className="mt-1 font-semibold">{formatBusinessDate(line.nextCutDate)}</dd>
      </div>
      <div>
        <dt className="text-xs font-semibold text-muted-foreground">Fecha límite de pago</dt>
        <dd className="mt-1 font-semibold">{formatBusinessDate(line.nextDueDate)}</dd>
      </div>
      <div>
        <dt className="text-xs font-semibold text-muted-foreground">
          Altura de mora · calificación
        </dt>
        <dd className="mt-1 font-semibold">
          {line.dpd} días · {line.rating}
        </dd>
      </div>
    </dl>
  );
}

function Figure({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-semibold text-muted-foreground">{label}</dt>
      <dd className="mt-1">
        <MoneyText
          value={value}
          size={emphasis ? 'lg' : 'md'}
          tone={emphasis ? 'primary' : 'default'}
        />
      </dd>
    </div>
  );
}
